import React, { useState } from "react";
import { Eye, EyeOff, Shield, CheckCircle, Sparkles, Lock } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * v2.37 — Auth UI primitives (premium SaaS proptech redesign)
 * Shared components used across /access, /login, /register, /team-login,
 * /forgot-password. Mobile-first; keeps existing data-testids intact.
 */

// =====================================================
// FloatingLabelInput — pill-rounded input with label that
// floats up on focus / when filled. Optional left icon.
// =====================================================
export function FloatingLabelInput({
  id, type = "text", label, value, onChange, icon: Icon,
  required = false, autoComplete, autoFocus, inputMode, maxLength,
  testid, error, hint, rightSlot, ...rest
}) {
  return (
    <div className="float-label-wrap relative">
      <div className="relative">
        {Icon && (
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400">
            <Icon size={16} />
          </span>
        )}
        <input
          id={id}
          type={type}
          value={value ?? ""}
          onChange={onChange}
          required={required}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          inputMode={inputMode}
          maxLength={maxLength}
          placeholder=" "
          data-testid={testid}
          className={`peer w-full h-14 pt-5 pb-1 pr-4 ${Icon ? "pl-11" : "pl-4"} ${rightSlot ? "pr-12" : ""} bg-zinc-50 border ${
            error ? "border-red-300 focus:border-red-500 focus:ring-red-100" : "border-zinc-200 hover:bg-white focus:bg-white focus:border-[#0055FF] focus:ring-[#0055FF]/15"
          } rounded-xl text-[15px] font-medium text-zinc-900 transition-all focus:outline-none focus:ring-4`}
          {...rest}
        />
        <label
          htmlFor={id}
          className={`absolute ${Icon ? "left-11" : "left-4"} top-1/2 -translate-y-1/2 text-[13px] font-medium text-zinc-500`}
        >
          {label}
        </label>
        {rightSlot && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</span>
        )}
      </div>
      {error && <p className="mt-1.5 text-[11px] font-semibold text-red-600">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-[11px] text-zinc-400">{hint}</p>}
    </div>
  );
}

// Password input with show/hide toggle
export function PasswordInput({ value, onChange, label = "Password", testid, autoComplete = "current-password", required = true, ...rest }) {
  const [show, setShow] = useState(false);
  return (
    <FloatingLabelInput
      id={`pwd-${testid || "field"}`}
      type={show ? "text" : "password"}
      label={label}
      icon={Lock}
      value={value}
      onChange={onChange}
      required={required}
      autoComplete={autoComplete}
      testid={testid}
      rightSlot={
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
          aria-label={show ? "Hide password" : "Show password"}
          data-testid={`${testid}-toggle`}
        >
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      }
      {...rest}
    />
  );
}

// =====================================================
// GradientButton — premium primary CTA with shine
// =====================================================
export function GradientButton({ children, type = "button", disabled, onClick, testid, className = "", icon: Icon, loading, ...rest }) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      data-testid={testid}
      className={`group relative w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl btn-gradient text-[15px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none ${className}`}
      {...rest}
    >
      {loading ? (
        <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
      ) : (
        <>
          <span>{children}</span>
          {Icon && <Icon size={16} className="transition-transform group-hover:translate-x-1" />}
        </>
      )}
    </button>
  );
}

// =====================================================
// TrustBadgeStrip — compact horizontal trust signals
// =====================================================
export function TrustBadgeStrip({ items, className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-5 gap-y-2 text-[10.5px] uppercase tracking-[0.18em] font-bold text-zinc-500 ${className}`} data-testid="trust-badges">
      {items.map((t) => (
        <span key={t} className="inline-flex items-center gap-1.5">
          <Shield size={11} className="text-[#0055FF]" />
          {t}
        </span>
      ))}
    </div>
  );
}

// =====================================================
// AuthSplitLeft — premium left rail for desktop split-screen
// (Login / Register / Team-Login / Forgot-Password). Pulls
// background image + animated stats + testimonial card.
// =====================================================
const HERO_BG_DEFAULT = "https://images.unsplash.com/photo-1761312387939-e6c1897ed5bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjBnbGFzcyUyMGJ1aWxkaW5nJTIwc2t5bGluZXxlbnwwfHx8fDE3NzgxMzYwNTV8MA&ixlib=rb-4.1.0&q=85";

export function AuthSplitLeft({
  eyebrow = "HouseEntry · Proptech Platform",
  heading,
  sub,
  quote = "HouseEntry transformed our lead management. A must-have for modern dealers.",
  attribution = "Verified Dealer · Dwarka Mor",
  bgUrl = HERO_BG_DEFAULT,
  stats = [
    { value: "500+", label: "Verified Listings" },
    { value: "100+", label: "Trusted Dealers" },
    { value: "Live", label: "Lead System" },
    { value: "24×7", label: "Response Teams" },
  ],
}) {
  return (
    <div className="hidden lg:flex relative flex-col justify-between p-12 xl:p-16 overflow-hidden bg-zinc-950 text-white" data-testid="auth-split-left">
      {/* Background image */}
      <img
        src={bgUrl}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-overlay"
        loading="lazy"
      />
      {/* Gradient blobs */}
      <div className="auth-blobs" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A1A]/50 via-transparent to-[#001233]/70" />

      {/* Top: brand */}
      <div className="relative z-10">
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-blue-300 mb-3 inline-flex items-center gap-2">
          <Sparkles size={13} /> {eyebrow}
        </div>
        <h2 className="font-heading font-bold text-4xl xl:text-5xl tracking-tight leading-[1.05] max-w-md">
          {heading}
        </h2>
        {sub && <p className="text-[15px] text-zinc-300 mt-4 max-w-md leading-relaxed">{sub}</p>}
      </div>

      {/* Middle: stats */}
      <div className="relative z-10 grid grid-cols-2 gap-6 my-10 max-w-md">
        {stats.map((s, i) => (
          <div key={s.label} className="rise" style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
            <div className="font-heading font-bold text-3xl tracking-tight text-white">{s.value}</div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-zinc-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Bottom: testimonial */}
      <div className="relative z-10 auth-glass-dark rounded-2xl p-5 max-w-md backdrop-blur-md" data-testid="auth-split-testimonial">
        <p className="text-[14px] italic text-zinc-200 leading-relaxed">"{quote}"</p>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300 mt-3">{attribution}</div>
      </div>
    </div>
  );
}

// =====================================================
// SecurityStrip — green/blue strip used on Team Login
// =====================================================
export function SecurityStrip({ label = "Protected Internal System", className = "" }) {
  return (
    <div className={`inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 px-3.5 py-2 rounded-full text-[11px] font-bold uppercase tracking-[0.16em] ${className}`} data-testid="security-strip">
      <Shield size={12} className="text-green-600" />
      {label}
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
    </div>
  );
}

// =====================================================
// AuthFooterNote — "Your data is securely protected."
// =====================================================
export function AuthFooterNote({ className = "" }) {
  return (
    <p className={`text-center text-[11px] text-zinc-400 inline-flex items-center justify-center gap-1.5 ${className}`}>
      <Lock size={11} /> Your data is securely protected · 256-bit TLS
    </p>
  );
}

// =====================================================
// ContinueAsBanner — shows "you're already signed in"
// =====================================================
export function ContinueAsBanner({ user, onContinue, testid = "continue-as-banner" }) {
  if (!user) return null;
  return (
    <div className="mb-5 p-3.5 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3" data-testid={testid}>
      <CheckCircle size={20} className="text-green-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-green-700">You're already signed in</div>
        <div className="text-[13px] font-bold text-zinc-900 truncate">{user.name || user.email}</div>
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="shrink-0 h-9 px-4 rounded-lg btn-gradient text-[12.5px] font-semibold inline-flex items-center gap-1.5"
        data-testid={`${testid}-continue-btn`}
      >
        Continue →
      </button>
    </div>
  );
}

// =====================================================
// AuthHeader — minimal logo bar shown on auth pages so
// the existing site Header can be hidden inside the split.
// =====================================================
export function AuthHeader({ showBack = false, backTo = "/" }) {
  return (
    <div className="flex items-center justify-between mb-8">
      <Link to="/" className="flex items-center gap-2.5" data-testid="auth-header-logo">
        <div className="w-9 h-9 rounded-lg bg-zinc-900 flex items-center justify-center">
          <span className="text-white font-heading font-bold text-[14px]">HE</span>
        </div>
        <div className="leading-tight">
          <div className="font-heading font-bold text-[14.5px] text-zinc-900">
            House<span className="text-[#E53935]">Entry</span> <span className="text-zinc-500 font-medium">Solutions</span>
          </div>
          <div className="text-[9.5px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Verified Deals · Smarter Decisions</div>
        </div>
      </Link>
      {showBack && (
        <Link to={backTo} className="text-[12px] font-semibold text-zinc-600 hover:text-[#0055FF]" data-testid="auth-header-back">← Back</Link>
      )}
    </div>
  );
}
